import React, {useState} from "react";
import WaterFishProtectionView from "./WaterFishProtectionView";
import {FormProvider, useForm} from "react-hook-form";
import { NetworkCalls } from "../../NetworkCalls";
import moment from "moment-timezone";
import {Fish} from "../../dtos/Fish";
import {Alert, Button} from "react-bootstrap";
import {FishProtectionArray, FishProtectionUpdateDTO} from "../../dtos/FishProtectionDTO";

type FishProtectionsViewProps = {
    embedded?: boolean;
};

export default function FishProtectionsView({embedded = false}: FishProtectionsViewProps) {

    const [loaded, setLoaded] = useState<boolean>(false);
    const [loadError, setLoadError] = useState<boolean>(false);
    const [availableFishes, setAvailableFishes] = useState<Fish[]>([]);

    const getInitValue = async (): Promise<FishProtectionArray> => {
        try {
            const orgaFishes = await NetworkCalls.getOrganisationFishes();
            setAvailableFishes(orgaFishes);

            const list = await NetworkCalls.listOrgaFishProtectionTimes();
            setLoaded(true);
            return {
                values: list,
            }
        } catch (error) {
            setLoadError(true);
            console.error(error);
            return { values: [] };
        }
    }

    const formMethods = useForm<FishProtectionArray>({
        defaultValues: getInitValue,
        shouldUnregister: false,
    });

    const { handleSubmit, reset } = formMethods; // deconstruct, to use some methods here

    const normalizeIso = (
        value: string | Date | null | undefined,
        isEnd: boolean
    ) => {
        if (!value) {
            return null;
        }
        // For saving we ALWAYS interpret values in the browser timezone
        // and then convert to UTC (Z) for sending to the backend.
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

        // If value is a Date, interpret in browser tz and convert to UTC
        if (value instanceof Date) {
            const m = moment(value).tz(tz);
            const target = isEnd ? m.endOf('day') : m.startOf('day');
            return target.clone().utc().format("YYYY-MM-DD[T]HH:mm:ss[Z]");
        }

        if (typeof value === 'string') {
            // date-only string (YYYY-MM-DD)
            if (!value.includes('T')) {
                const m = moment.tz(value, 'YYYY-MM-DD', tz);
                const target = isEnd ? m.endOf('day') : m.startOf('day');
                return target.clone().utc().format("YYYY-MM-DD[T]HH:mm:ss[Z]");
            }

            // datetime string with 'T'
            // if it already has timezone information or trailing Z, parse respecting that and convert to UTC
            if (/[zZ]$|[-+]\d{2}:\d{2}$/.test(value)) {
                const m = moment.parseZone(value);
                return m.utc().format("YYYY-MM-DD[T]HH:mm:ss[Z]");
            }

            // otherwise interpret as local datetime in browser tz and convert to UTC
            const m = moment.tz(value, tz);
            const target = isEnd ? m.endOf('day') : m.startOf('day');
            return target.clone().utc().format("YYYY-MM-DD[T]HH:mm:ss[Z]");
        }
        return null;
    };

    const onSubmit = (data: FishProtectionArray) => {
        setLoaded(false);
        const payload: FishProtectionUpdateDTO[] = data.values.map((item) => ({
            id: item.id || undefined,
            orgaFishId: item.orgaFishId,
            fromIso: item.hasProtectionTime
                ? normalizeIso(item.fromIso, false)
                : null,
            untilIso: item.hasProtectionTime
                ? normalizeIso(item.untilIso, true)
                : null,
            // also include legacy keys expected by some backends
            from: item.hasProtectionTime
                ? normalizeIso(item.fromIso, false)
                : null,
            until: item.hasProtectionTime
                ? normalizeIso(item.untilIso, true)
                : null,
            min: item.min ?? 0,
            max: item.max ?? 0
        }));
        NetworkCalls.updateOrgaFishProtectionTimes(payload)
            .then((response) => {
                if (response) {
                    // Ensure legacy `from`/`until` fields reflect the canonical UTC `fromIso`/`untilIso`
                    const normalizedResponse = response.map((r) => ({
                        ...r,
                        from: (r as any).fromIso ?? (r as any).from,
                        until: (r as any).untilIso ?? (r as any).until,
                    }));

                    reset({ values: normalizedResponse }, {
                        keepIsSubmitted: false,
                        keepDefaultValues: false,
                        keepValues: false,
                        keepErrors: false,
                        keepDirty: false,
                        keepTouched: false,
                    });
                } else {
                    setLoadError(true);
                }
            }).catch(error => {
                setLoadError(true);
                console.error(error);
        }).finally(() => {
            setLoaded(true);
        });
    };

    const renderForm = () => {
        if (!loaded) {
            return <>Lade Daten...</>
        }

        if (loadError) {
            return <>
                <Alert variant="danger">
                    Fehler beim Laden der Daten. Bitte versuchen Sie die Seite neu zu laden.
                </Alert>
            </>
        }

        return <>
            <FormProvider {... formMethods} >
                <form onSubmit={handleSubmit(onSubmit)}>
                    <WaterFishProtectionView
                        availableFishes={availableFishes}
                        visible={true}
                        formPath={'values'}
                    />
                    <Button type={"submit"} variant={"primary"} className={"mt-3"} >Speichern</Button>
                </form>
            </FormProvider>
        </>
    }

    if (embedded) {
        return <>{renderForm()}</>;
    }

    return (
        <div className="page-body">
            <div className="container-xxl pt-5">
                <h1>Schonzeiten & Entnahmefenster</h1>
                {renderForm()}
            </div>
        </div>
    );
}
